import { type CityPlan, type Point, type RoadSegment } from './cityPlan';
import { mulberry32 } from './rng';
import { spriteImage } from './sprites';

export interface DioramaProjection {
  toCanvas(position: { x: number; y: number }): { x: number; y: number };
  unitScale: number;
}

export const NIGHT = {
  paper: '#1d2a3f',
  paperFibre: '#2b3b57',
  paperSpeck: '#121b2b',
  cardEdge: '#cec0a0',
  streetEdge: '#8a826d',
  curb: '#a39a82',
  blockRim: '#8f8672',
  parapet: '#c9bea3',
  pavement: '#36425c',
  cobble: '#465370',
  road: '#435372',
  alley: '#384663',
  plaza: '#4a5a7a',
  water: '#1f4a63',
  waterTop: '#2e6b86',
  waterSheen: '#6fb0c8',
  dusk: 'rgba(6, 10, 24, 0.5)',
  castShadow: 'rgba(3, 6, 14, 0.7)',
  lampCore: '#ffe2a8',
  lampGlow: 'rgba(255, 158, 64, 0.3)',
} as const;

export const PROPS = {
  lamp: 'props-00',
  lampLit: 'props-01',
  source: 'props-02',
  sink: 'props-03',
} as const;

const PAPER_TILE = 256;
const COBBLE_TILE = 14;
const LAMP_RADIUS = 50;

const propImages = new Map<string, HTMLImageElement>();
let propsPending: Promise<void> | null = null;

export function ensurePropsLoaded(): Promise<void> {
  if (propsPending) return propsPending;
  if (typeof Image === 'undefined') return (propsPending = Promise.resolve());
  const base = import.meta.env?.BASE_URL;
  const root = `${typeof base === 'string' && base.length > 0 ? base : '/'}props-diorama`;
  propsPending = Promise.all(
    Object.values(PROPS).map(
      (id) =>
        new Promise<void>((resolve) => {
          const image = new Image();
          image.onload = () => {
            propImages.set(id, image);
            resolve();
          };
          image.onerror = () => resolve();
          image.src = `${root}/${id}.webp`;
        }),
    ),
  ).then(() => undefined);
  return propsPending;
}

export function propImage(id: string): HTMLImageElement | null {
  return propImages.get(id) ?? null;
}

function paperTile(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const tile = document.createElement('canvas');
  tile.width = PAPER_TILE;
  tile.height = PAPER_TILE;
  const ctx = tile.getContext('2d');
  if (!ctx) return null;

  const random = mulberry32(1871);
  ctx.fillStyle = NIGHT.paper;
  ctx.fillRect(0, 0, PAPER_TILE, PAPER_TILE);

  ctx.lineCap = 'round';
  for (let i = 0; i < 140; i++) {
    const x = random() * PAPER_TILE;
    const y = random() * PAPER_TILE;
    const angle = random() * Math.PI;
    const length = 4 + random() * 14;
    ctx.strokeStyle = random() < 0.7 ? NIGHT.paperFibre : NIGHT.paperSpeck;
    ctx.globalAlpha = 0.25 + random() * 0.35;
    ctx.lineWidth = 0.6 + random() * 0.8;
    for (const [dx, dy] of [
      [0, 0],
      [-PAPER_TILE, 0],
      [0, -PAPER_TILE],
      [-PAPER_TILE, -PAPER_TILE],
    ] as const) {
      ctx.beginPath();
      ctx.moveTo(x + dx, y + dy);
      ctx.lineTo(x + dx + Math.cos(angle) * length, y + dy + Math.sin(angle) * length);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
  return tile;
}

let cachedTile: HTMLCanvasElement | null | undefined;
let cachedCobble: HTMLCanvasElement | null | undefined;

function cobbleTile(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const tile = document.createElement('canvas');
  tile.width = COBBLE_TILE;
  tile.height = COBBLE_TILE;
  const ctx = tile.getContext('2d');
  if (!ctx) return null;
  const half = COBBLE_TILE / 2;
  ctx.fillStyle = NIGHT.pavement;
  ctx.fillRect(0, 0, COBBLE_TILE, COBBLE_TILE);
  ctx.fillStyle = NIGHT.cobble;
  for (const [x, y] of [
    [0, 0],
    [half, 0],
    [-half / 2, half],
    [half / 2, half],
    [half * 1.5, half],
  ] as const) {
    ctx.beginPath();
    ctx.roundRect(x + 0.8, y + 0.8, half - 1.6, half - 1.6, 1.6);
    ctx.fill();
  }
  return tile;
}

export function cobblePattern(ctx: CanvasRenderingContext2D): CanvasPattern | string {
  if (cachedCobble === undefined) cachedCobble = cobbleTile();
  return (cachedCobble && ctx.createPattern(cachedCobble, 'repeat')) ?? NIGHT.pavement;
}

export function fillNightPaper(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  if (cachedTile === undefined) cachedTile = paperTile();
  const pattern = cachedTile ? ctx.createPattern(cachedTile, 'repeat') : null;
  ctx.fillStyle = pattern ?? NIGHT.paper;
  ctx.fillRect(x, y, width, height);
}

export function strokePolyline(
  ctx: CanvasRenderingContext2D,
  points: readonly Point[],
  projection: DioramaProjection,
): void {
  ctx.beginPath();
  points.forEach((point, index) => {
    const p = projection.toCanvas(point);
    if (index === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();
}

export function strokeSegments(
  ctx: CanvasRenderingContext2D,
  segments: readonly RoadSegment[],
  projection: DioramaProjection,
  extraPixels: number,
): void {
  for (const segment of segments) {
    const a = projection.toCanvas(segment.a);
    const b = projection.toCanvas(segment.b);
    ctx.lineWidth = Math.max(1, segment.width * projection.unitScale + extraPixels);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
}

export function fillPlazas(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  extraPixels: number,
): void {
  for (const plaza of plan.plazas) {
    const center = projection.toCanvas(plaza.center);
    ctx.beginPath();
    ctx.arc(center.x, center.y, plaza.radius * projection.unitScale + extraPixels, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function paintCanal(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
): void {
  const width = plan.canal.width * projection.unitScale;
  if (plan.canal.path.length < 2 || width <= 0) return;

  ctx.strokeStyle = NIGHT.cardEdge;
  ctx.lineWidth = width + 4;
  strokePolyline(ctx, plan.canal.path, projection);

  ctx.strokeStyle = NIGHT.water;
  ctx.lineWidth = width;
  strokePolyline(ctx, plan.canal.path, projection);

  ctx.globalAlpha = 0.75;
  ctx.strokeStyle = NIGHT.waterTop;
  ctx.lineWidth = width * 0.62;
  strokePolyline(ctx, plan.canal.path, projection);

  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = NIGHT.waterSheen;
  ctx.lineWidth = Math.max(1, width * 0.12);
  strokePolyline(ctx, plan.canal.path, projection);
  ctx.globalAlpha = 1;
}

function strokeLanes(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  extraPixels: number,
): void {
  strokeSegments(ctx, plan.alleys, projection, extraPixels);
  strokeSegments(ctx, plan.sideStreets, projection, extraPixels);
}

export function paintDeck(ctx: CanvasRenderingContext2D, deck: BridgeDeck): void {
  const { corners, rails } = deck;
  const outline = () => {
    ctx.beginPath();
    corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  };

  ctx.save();
  ctx.shadowColor = NIGHT.castShadow;
  ctx.shadowBlur = 12;
  ctx.shadowOffsetX = 3;
  ctx.shadowOffsetY = 9;
  ctx.fillStyle = cobblePattern(ctx);
  outline();
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = NIGHT.parapet;
  outline();
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.lineCap = 'butt';
  for (const [from, to] of rails) {
    ctx.strokeStyle = NIGHT.castShadow;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y + 2);
    ctx.lineTo(to.x, to.y + 2);
    ctx.stroke();
    ctx.strokeStyle = NIGHT.parapet;
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    ctx.fillStyle = NIGHT.parapet;
    const span = Math.hypot(to.x - from.x, to.y - from.y);
    const posts = Math.max(2, Math.round(span / 12));
    for (let i = 0; i <= posts; i++) {
      const t = i / posts;
      ctx.fillRect(from.x + (to.x - from.x) * t - 2, from.y + (to.y - from.y) * t - 4, 4, 5);
    }
  }
  ctx.restore();
}

function paintBridges(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
): void {
  for (const crossing of plan.crossings) {
    const deck = bridgeDeck(plan, crossing, projection);
    if (deck) paintDeck(ctx, deck);
  }
}

export interface BridgeDeck {
  readonly corners: readonly Point[];
  readonly rails: readonly (readonly [Point, Point])[];
}

export function bridgeDeck(
  plan: CityPlan,
  crossing: CityPlan['crossings'][number],
  projection: DioramaProjection,
): BridgeDeck | null {
  const over = plan.roads.find((r) => r.edgeId === crossing.over);
  const under = plan.roads.find((r) => r.edgeId === crossing.under);
  if (!over || !under) return null;

  return deckAcross(
    { a: projection.toCanvas(over.a), b: projection.toCanvas(over.b) },
    over.width * projection.unitScale,
    { a: projection.toCanvas(under.a), b: projection.toCanvas(under.b) },
    under.width * projection.unitScale,
    projection.toCanvas(crossing.at),
  );
}

export function deckAcross(
  over: { readonly a: Point; readonly b: Point },
  overWidth: number,
  under: { readonly a: Point; readonly b: Point },
  underWidth: number,
  at: Point,
): BridgeDeck | null {
  const { a, b } = over;
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  const underLength = Math.hypot(under.b.x - under.a.x, under.b.y - under.a.y);
  if (length === 0 || underLength === 0) return null;

  const dx = (b.x - a.x) / length;
  const dy = (b.y - a.y) / length;
  const sine = Math.abs(dx * (under.b.y - under.a.y) - dy * (under.b.x - under.a.x)) / underLength;
  const half = Math.min(90, (underWidth + 5) / 2 / Math.max(0.3, sine) + 9);
  const side = (overWidth + 5) / 2 + 2;

  const point = (along: number, across: number): Point => ({
    x: at.x + dx * along - dy * across,
    y: at.y + dy * along + dx * across,
  });
  return {
    corners: [point(-half, -side), point(half, -side), point(half, side), point(-half, side)],
    rails: [
      [point(-half, -side), point(half, -side)],
      [point(-half, side), point(half, side)],
    ],
  };
}

function paintStreetLevel(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  width: number,
  height: number,
): void {
  ctx.fillStyle = NIGHT.alley;
  ctx.fillRect(0, 0, width, height);
  paintCanal(ctx, plan, projection);

  ctx.strokeStyle = NIGHT.curb;
  ctx.fillStyle = NIGHT.curb;
  strokeSegments(ctx, plan.roads, projection, 5);
  fillPlazas(ctx, plan, projection, 3);

  const cobbles = cobblePattern(ctx);
  ctx.strokeStyle = cobbles;
  ctx.fillStyle = cobbles;
  strokeSegments(ctx, plan.roads, projection, 0);
  fillPlazas(ctx, plan, projection, 0);

  paintBridges(ctx, plan, projection);
}

// The blocks are the raised card between the streets: the whole sheet minus
// every street, with a thin cut edge where a street was taken out.
export function paintBlocks(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  width: number,
  height: number,
  rim = 3,
  rimColor: string = NIGHT.blockRim,
): void {
  fillNightPaper(ctx, 0, 0, width, height);

  const canalWidth = plan.canal.width * projection.unitScale;
  const cut = (rim: number) => {
    strokeLanes(ctx, plan, projection, rim);
    strokeSegments(ctx, plan.roads, projection, 5 + rim);
    fillPlazas(ctx, plan, projection, 3 + rim / 2);
    if (plan.canal.path.length >= 2 && canalWidth > 0) {
      ctx.lineWidth = canalWidth + 4 + rim;
      strokePolyline(ctx, plan.canal.path, projection);
    }
  };

  ctx.strokeStyle = rimColor;
  ctx.fillStyle = rimColor;
  cut(rim);

  ctx.globalCompositeOperation = 'destination-out';
  cut(0);
  ctx.globalCompositeOperation = 'source-over';
}

export function paintBuildings(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
): void {
  for (const building of plan.buildings) {
    const image = building.spriteId ? spriteImage(building.spriteId) : null;
    if (!image) continue;
    const foot = projection.toCanvas(building.anchor);
    const width = building.width * projection.unitScale;
    const height = building.height * projection.unitScale;
    if (width < 1 || height < 1) continue;
    ctx.drawImage(image, foot.x - width / 2, foot.y - height, width, height);
  }
}

function paintLamplight(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: DioramaProjection,
  width: number,
  height: number,
): void {
  const lamps = plan.plazas.map((plaza) => projection.toCanvas(plaza.center));
  const radius = LAMP_RADIUS;

  ctx.globalCompositeOperation = 'screen';
  for (const lamp of lamps) {
    const glow = ctx.createRadialGradient(lamp.x, lamp.y, 0, lamp.x, lamp.y, radius);
    glow.addColorStop(0, NIGHT.lampGlow);
    glow.addColorStop(1, 'rgba(255, 176, 84, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(lamp.x - radius, lamp.y - radius, radius * 2, radius * 2);
  }
  ctx.globalCompositeOperation = 'source-over';

  const vignette = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.35,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.75,
  );
  vignette.addColorStop(0, 'rgba(4, 7, 16, 0)');
  vignette.addColorStop(1, 'rgba(4, 7, 16, 0.55)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
}

export function layer(width: number, height: number, pixelRatio: number) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * pixelRatio));
  canvas.height = Math.max(1, Math.round(height * pixelRatio));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  return { canvas, ctx };
}

export function dropLayer(
  target: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  width: number,
  height: number,
  lift: number,
): void {
  target.save();
  target.shadowColor = NIGHT.castShadow;
  target.shadowBlur = lift * 2;
  target.shadowOffsetX = lift * 0.6;
  target.shadowOffsetY = lift;
  target.drawImage(source, 0, 0, width, height);
  target.restore();
}

export function bakeDioramaLayer(
  plan: CityPlan,
  projection: DioramaProjection,
  width: number,
  height: number,
  pixelRatio: number,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  if (!(width > 0) || !(height > 0)) return null;

  const base = layer(width, height, pixelRatio);
  const blocks = layer(width, height, pixelRatio);
  const buildings = layer(width, height, pixelRatio);
  if (!base || !blocks || !buildings) return null;

  paintStreetLevel(base.ctx, plan, projection, width, height);
  paintBlocks(blocks.ctx, plan, projection, width, height);
  dropLayer(base.ctx, blocks.canvas, width, height, 3);
  base.ctx.fillStyle = NIGHT.dusk;
  base.ctx.fillRect(0, 0, width, height);

  paintBuildings(buildings.ctx, plan, projection);
  dropLayer(base.ctx, buildings.canvas, width, height, 6);

  paintLamplight(base.ctx, plan, projection, width, height);
  return base.canvas;
}
